import type { CentralIconBaseProps } from 'central-icons/CentralIconBase';
import { IconBookmark } from 'central-icons/IconBookmark';
import { IconMagnifyingGlass } from 'central-icons/IconMagnifyingGlass';
import { IconSettingsSliderHor } from 'central-icons/IconSettingsSliderHor';
import type { ComponentType, KeyboardEvent } from 'react';

import { Option } from '@revelio/ui';

import styles from './impersonationCards.module.css';

export type ImpersonationMode = 'preset' | 'search' | 'custom';

interface ModeOption {
  mode: ImpersonationMode;
  title: string;
  description: string;
  icon: ComponentType<CentralIconBaseProps>;
}

const MODE_OPTIONS: ModeOption[] = [
  {
    mode: 'preset',
    title: 'Use a preset',
    description: 'Quickly apply a CST or Free Trial setup',
    icon: IconBookmark,
  },
  {
    mode: 'search',
    title: 'Search a user',
    description: 'Impersonate an existing client user',
    icon: IconMagnifyingGlass,
  },
  {
    mode: 'custom',
    title: 'Custom overrides',
    description: 'Manually configure tabs, role, datasets…',
    icon: IconSettingsSliderHor,
  },
];

interface ImpersonationModePickerProps {
  mode: ImpersonationMode;
  onChange: (mode: ImpersonationMode) => void;
}

export function ImpersonationModePicker({
  mode,
  onChange,
}: ImpersonationModePickerProps) {
  // Roving tabindex: only the active tab is reachable by Tab, and Left/Right
  // move between tabs — the expected keyboard contract for role="tablist".
  const ARROW_DELTAS: Record<string, number> = {
    ArrowRight: 1,
    ArrowLeft: -1,
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    index: number
  ) => {
    const delta = ARROW_DELTAS[event.key];

    if (delta === undefined) {
      return;
    }

    event.preventDefault();

    const next =
      MODE_OPTIONS[(index + delta + MODE_OPTIONS.length) % MODE_OPTIONS.length];

    if (next) {
      onChange(next.mode);
    }
  };

  return (
    <div
      role="tablist"
      aria-label="Impersonation mode"
      className={styles.modeList}
    >
      {MODE_OPTIONS.map((option, index) => {
        const isActive = mode === option.mode;
        const Icon = option.icon;

        return (
          <Option
            key={option.mode}
            role="tab"
            id={`impersonation-tab-${option.mode}`}
            aria-controls={`impersonation-panel-${option.mode}`}
            tabIndex={isActive ? 0 : -1}
            isSelected={isActive}
            onClick={() => onChange(option.mode)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            startSlot={
              <span className={styles.iconBadge}>
                <Icon size={16} ariaHidden={true} />
              </span>
            }
            startSlotAlignment="center"
            className={styles.modeCard}
            data-testid={`impersonation-mode-${option.mode}`}
          >
            <span className={styles.cardTitle}>{option.title}</span>
            <span className={styles.cardDescription}>{option.description}</span>
          </Option>
        );
      })}
    </div>
  );
}
